import { useEffect, useMemo, useState } from 'react';
import { useDocumentTitle, usePersistedColumnSizing, useSearchParams } from '@shared/hooks';
import { StatusLabel, Pagination, TableActionButtons, CopyToClipboardButton } from '@shared/components';
import { Subtitle1, createTableColumn, useTableFeatures } from '@fluentui/react-components';
import { calculateRowsPerPage } from '@shared/helpers';

import { useAccessLogs } from '../api/accessLogs.api';
import { TAccessLog, TGetAccessLogsResult } from '../types/accessLogs.types';
import AccessLogsTable from '../components/accessLogs/AccessLogsTable';
import { ACCESS_LOGS_TABLE_ID, ACCESS_LOGS_UI_MARGIN_TOP } from '../constants';
import { TTableRow } from '../../../shared/types';

const AccessLogs = () => {
  const { searchParams, change } = useSearchParams();
  const page = Number(searchParams.get('page'));
  const pageSize = Number(searchParams.get('pageSize'));

  useEffect(() => {
    if (Number(page) < 1) change('page', '1');
    if (Number(pageSize) < 1) change('pageSize', calculateRowsPerPage(ACCESS_LOGS_UI_MARGIN_TOP));
  }, [page, pageSize]);

  const { data: apiData, error } = useAccessLogs({ page, pageSize });
  const [data, setData] = useState<TGetAccessLogsResult | undefined>();

  useEffect(() => {
    if (error) {
      console.error('Error fetching access logs:', error);
      setData(undefined);
      return;
    }
    if (!apiData) return;
    setData(apiData);
  }, [apiData, error]);

  useDocumentTitle('Access logs');

  return (
    <StatusLabel
      data={data}
      loadingMessage="Loading access logs..."
      errorMessage="Loading access logs failed!"
      render={(resolvedData) => <AccessLogsContent total={resolvedData.total} items={resolvedData.items} />}
    />
  );
};

export default AccessLogs;

const columnIds = ['description', 'date', 'initiator'] as const;

const columnSizingParams = {
  autoFitColumns: false,
  columnSizingOptions: {
    description: { idealWidth: 520, minWidth: 280 },
    date: { defaultWidth: 150 },
    initiator: { defaultWidth: 150 },
  },
};

const AccessLogsContent = ({ items, total }: TGetAccessLogsResult) => {
  const columns = useMemo(() => columnIds.map((columnId) => createTableColumn<TAccessLog>({ columnId })), []);
  const { columnSizing_unstable, getRows } = useTableFeatures({ columns, items, getRowId: (r) => r.id }, [
    usePersistedColumnSizing(ACCESS_LOGS_TABLE_ID, columnSizingParams),
  ]);

  const rows: TTableRow<TAccessLog>[] = getRows((row) => ({
    ...row,
    key: row.rowId as string,
    actionButton: (
      <TableActionButtons alwaysVisible>
        <CopyToClipboardButton value={row.rowId as string} label="Log ID" />
      </TableActionButtons>
    ),
  }));

  return (
    <div className="w-full max-w-screen-2xl pr-8">
      <p className="pb-4">
        <Subtitle1>Access logs</Subtitle1>
      </p>
      {rows.length ? (
        <>
          <AccessLogsTable rows={rows} columnSizing={columnSizing_unstable} />
          <Pagination total={total} margin={ACCESS_LOGS_UI_MARGIN_TOP} />
        </>
      ) : (
        <p>No access logs found!</p>
      )}
    </div>
  );
};
